import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AIProcessingPhase } from 'shared/components/AIDocumentScanLoader';
import FileUpload from 'features/onboarding/components/upload/FileUpload';
import DocumentInstructions from 'features/onboarding/components/upload/DocumentInstructions';
import OcrConflictModal from 'features/onboarding/components/upload/OcrConflictModal';
import RemoveDocumentModal from 'features/onboarding/components/upload/RemoveDocumentModal';
import { useStepDocumentUpload } from 'features/onboarding/hooks/useStepDocumentUpload';
import type { DocumentKey } from 'features/onboarding/types/onboarding.types';

interface StepDocumentSectionProps {
  docKey: DocumentKey;
  /** When true, the upload triggers OCR extraction to prefill form fields. */
  ocr?: boolean;
  required?: boolean;
  error?: string;
  /** Override accepted MIME types passed to the dropzone. */
  accept?: Record<string, string[]>;
}

/**
 * Reusable document slot rendered at the top of a form step: the upload
 * dropzone, an explanation of what we'll extract (OCR slots only), and
 * collapsible instructions on how to obtain the document. Wires up the OCR
 * conflict + dependent-field reset modals via `useStepDocumentUpload`.
 */
const StepDocumentSection: React.FC<StepDocumentSectionProps> = ({
  docKey,
  ocr = false,
  required,
  error,
  accept,
}) => {
  const { t } = useTranslation();
  const upload = useStepDocumentUpload({ docKey, ocr });

  const label = t(`documents.${docKey}`) as string;

  // Drives the inline AI scan loader inside the upload dropzone.
  const [isUploading, setIsUploading] = useState(false);
  const [processingPhase, setProcessingPhase] = useState<AIProcessingPhase | null>(null);

  useEffect(() => {
    if (ocr && isUploading) setProcessingPhase('uploading');
  }, [ocr, isUploading]);

  useEffect(() => {
    if (!ocr) return;
    if (upload.status === 'extracting') {
      setProcessingPhase('extracting');
    } else if (upload.status === 'success' || upload.status === 'failed') {
      setProcessingPhase((prev) => (prev ? 'complete' : null));
    }
  }, [ocr, upload.status]);

  // Reset loader if the upload failed before OCR started.
  useEffect(() => {
    if (
      ocr &&
      !isUploading &&
      upload.status === 'idle' &&
      processingPhase === 'uploading'
    ) {
      setProcessingPhase(null);
    }
  }, [ocr, isUploading, upload.status, processingPhase]);

  const statusHint = (() => {
    if (!ocr || processingPhase) return undefined;
    switch (upload.status) {
      case 'success':
        return t('documents.extracted') as string;
      case 'failed':
        return t('documents.extractionFailed') as string;
      default:
        return undefined;
    }
  })();

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
      {ocr && !processingPhase && (
        <div className="flex items-start gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-white text-gray-600 flex items-center justify-center flex-shrink-0 border border-gray-200">
            <Sparkles size={16} />
          </span>
          <p className="text-sm text-gray-700 leading-relaxed">
            {t('documents.extractionHelper')}
          </p>
        </div>
      )}

      <FileUpload
        label={label}
        required={required}
        value={upload.file}
        accept={accept}
        onChange={upload.onChange}
        onUploaded={upload.onUploaded}
        onRequestRemove={upload.onRequestRemove}
        onUploadingChange={ocr ? setIsUploading : undefined}
        hideInlineLoader={ocr}
        processingPhase={ocr ? processingPhase : undefined}
        onProcessingDone={() => setProcessingPhase(null)}
        busy={upload.busy}
        busyLabel={t('documents.extracting') as string}
        error={error}
        hint={statusHint}
      />

      {ocr && upload.status === 'success' && !processingPhase && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <CheckCircle2 size={12} />
          {t('documents.extracted')}
        </span>
      )}
      {ocr && upload.status === 'failed' && !processingPhase && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
          <AlertTriangle size={12} />
          {t('documents.extractionFailed')}
        </span>
      )}

      <DocumentInstructions docKey={docKey} />

      <OcrConflictModal
        open={upload.conflictModal.open}
        conflicts={upload.conflictModal.conflicts}
        documentLabel={upload.conflictModal.documentLabel}
        onClose={upload.conflictModal.onClose}
        onResolve={upload.conflictModal.onResolve}
      />

      <RemoveDocumentModal
        open={upload.removeModal.open}
        docKey={upload.removeModal.docKey}
        documentLabel={upload.removeModal.documentLabel}
        dependents={upload.removeModal.dependents}
        onClose={upload.removeModal.onClose}
        onConfirm={upload.removeModal.onConfirm}
      />
    </div>
  );
};

export default StepDocumentSection;
